import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

async function apiFetch(path, { method = "GET", body } = {}) {
  const base = process.env.REACT_APP_API_BASE;
  if (!base) throw new Error("Missing REACT_APP_API_BASE in .env");

  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) throw new Error("Missing ID token (please sign out/in again)");

  const res = await fetch(`${base}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    const msg = data?.error || data?.message || `HTTP ${res.status}`;
    throw new Error(msg.includes("HTTP") ? msg : `${msg} (HTTP ${res.status})`);
  }
  return data;
}

function qsValue(name) {
  const p = new URLSearchParams(window.location.search);
  return p.get(name) || "";
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function fmtHMS(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

export default function WarrantyApprovePage() {
  const wrId = qsValue("wrId");

  // ✅ Updated: use new status endpoint (GET /warranty/status?wrId=...)
  const STATUS_PATH = useMemo(
    () => `/warranty/status?wrId=${encodeURIComponent(wrId)}`,
    [wrId]
  );

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [decision, setDecision] = useState("APPROVE"); // APPROVE | REJECT
  const [rejectReason, setRejectReason] = useState("");
  const [useMySignature, setUseMySignature] = useState(true);

  // New UX states
  const [phase, setPhase] = useState("FORM"); // FORM | SUBMITTED | PROCESSING | READY | DONE | ERROR
  const [statusObj, setStatusObj] = useState(null);
  const [polling, setPolling] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);

  const pollTimerRef = useRef(null);
  const startTsRef = useRef(0);

  useEffect(() => {
    if (!wrId) {
      setError("Missing wrId in URL.");
      setLoading(false);
      return;
    }

    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await apiFetch(STATUS_PATH, { method: "GET" });
        setStatusObj(data);

        // If already ready, show READY
        if (data?.approvalReady) {
          setPhase("READY");
        } else if (Number(data?.approvalNotifiedAt || 0) > 0) {
          // (optional) if you ever set notified without ready
          setPhase("READY");
        }
      } catch (e) {
        // If status endpoint not deployed yet, don't hard fail page.
        console.warn("Status load failed:", e?.message || e);
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [wrId, STATUS_PATH]);

  async function fetchStatusOnce() {
    const data = await apiFetch(STATUS_PATH, { method: "GET" });
    setStatusObj(data);
    return data;
  }

  async function startPollingUntilReady() {
    if (!wrId) return;

    setPolling(true);
    setPhase("PROCESSING");
    startTsRef.current = Date.now();
    setElapsedMs(0);

    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startTsRef.current);
    }, 250);

    const POLL_EVERY_MS = 2500;
    const GIVE_UP_AFTER_MS = 2 * 60 * 1000;

    try {
      while (true) {
        const data = await fetchStatusOnce();

        // ✅ Preferred “final” message:
        // If you want the page to say READY only after the email is sent, use approvalNotifiedAt > 0.
        // If you want READY as soon as PDFs are ready, use approvalReady.
        const isReady = !!data?.approvalReady;

        if (isReady) {
          setPhase("READY");
          break;
        }

        if (String(data?.approvalStatus || "").toUpperCase() === "REJECTED") {
          setPhase("DONE");
          break;
        }

        if (Date.now() - startTsRef.current > GIVE_UP_AFTER_MS) {
          setPhase("SUBMITTED");
          break;
        }

        await sleep(POLL_EVERY_MS);
      }
    } finally {
      setPolling(false);
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }
  }

  async function submitDecision() {
    if (!wrId) return;

    if (decision === "REJECT" && !String(rejectReason || "").trim()) {
      setError("Reject reason is required when Reject is selected.");
      return;
    }

    setBusy(true);
    setError("");

    try {
      await apiFetch(`/warranty/${encodeURIComponent(wrId)}/approve`, {
        method: "POST",
        body: {
          decision,
          rejectReason: decision === "REJECT" ? String(rejectReason || "").trim() : "",
          useMySignature: !!useMySignature,
        },
      });

      if (decision === "REJECT") {
        setPhase("DONE");
      } else {
        setPhase("SUBMITTED");
        startPollingUntilReady();
      }
    } catch (e) {
      setPhase("ERROR");
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  const cardStyle = {
    marginTop: 16,
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 12,
  };

  const monoStyle = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" };
  const pdfStatus = statusObj?.approvalPdfStatus || null;

  return (
    <div style={{ padding: 24, maxWidth: 720, margin: "0 auto" }}>
      <div style={{ fontSize: 18, fontWeight: 900 }}>Warranty Approval</div>
      <div style={{ marginTop: 6, color: "#64748b", fontSize: 12 }}>
        wrId: <span style={monoStyle}>{wrId || "-"}</span>
      </div>

      {error ? (
        <div style={{ marginTop: 12, color: "#b91c1c", fontWeight: 800 }}>
          {error}
        </div>
      ) : null}

      {loading ? <div style={{ marginTop: 12 }}>Loading...</div> : null}

      {/* Status / Progress Panel */}
      {phase !== "FORM" ? (
        <div style={cardStyle}>
          {phase === "SUBMITTED" ? (
            <>
              <div style={{ fontWeight: 900 }}>Submitted</div>
              <div style={{ marginTop: 8, color: "#334155", fontSize: 13 }}>
                Your approval was submitted. PDFs are being generated in the background.
              </div>
              <div style={{ marginTop: 10, color: "#64748b", fontSize: 12 }}>
                You can keep this page open to see when it becomes ready.
              </div>
            </>
          ) : null}

          {phase === "PROCESSING" ? (
            <>
              <div style={{ fontWeight: 900 }}>Approved — Generating PDFs…</div>
              <div style={{ marginTop: 8, color: "#334155", fontSize: 13 }}>
                Processing in progress. This may take a few seconds.
              </div>
              <div style={{ marginTop: 10, color: "#64748b", fontSize: 12 }}>
                Elapsed: <span style={monoStyle}>{fmtHMS(elapsedMs)}</span>
                {polling ? " · Checking status…" : null}
              </div>

              {pdfStatus ? (
                <div style={{ marginTop: 10, fontSize: 12, color: "#334155" }}>
                  <div style={{ fontWeight: 800, marginBottom: 6 }}>PDF status</div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <div>
                      Orange: <span style={monoStyle}>{String(pdfStatus.orange || "-")}</span>
                    </div>
                    <div>
                      Purple: <span style={monoStyle}>{String(pdfStatus.purple || "-")}</span>
                    </div>
                  </div>
                </div>
              ) : null}

              <div style={{ marginTop: 12 }}>
                <button
                  onClick={async () => {
                    setError("");
                    try {
                      await fetchStatusOnce();
                    } catch (e) {
                      setError(e.message || String(e));
                    }
                  }}
                  disabled={busy}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 10,
                    border: "1px solid #e2e8f0",
                    fontWeight: 900,
                    background: "white",
                    cursor: busy ? "not-allowed" : "pointer",
                  }}
                >
                  Refresh status
                </button>
              </div>
            </>
          ) : null}

          {phase === "READY" ? (
            <>
              <div style={{ fontWeight: 900, color: "#166534" }}>Approved PDFs are ready ✅</div>
              <div style={{ marginTop: 8, color: "#334155", fontSize: 13 }}>
                The Sales Admin will receive an email with the download links.
              </div>
              <div style={{ marginTop: 10, color: "#64748b", fontSize: 12 }}>
                You may close this page.
              </div>
            </>
          ) : null}

          {phase === "DONE" && decision === "REJECT" ? (
            <>
              <div style={{ fontWeight: 900 }}>Rejected ✅</div>
              <div style={{ marginTop: 8, color: "#334155", fontSize: 13 }}>
                Your rejection was submitted. The Sales Admin will be notified.
              </div>
              <div style={{ marginTop: 10, color: "#64748b", fontSize: 12 }}>
                You may close this page.
              </div>
            </>
          ) : null}

          {phase === "ERROR" ? (
            <>
              <div style={{ fontWeight: 900, color: "#b91c1c" }}>Submission failed</div>
              <div style={{ marginTop: 8, color: "#334155", fontSize: 13 }}>
                Please try again or contact support.
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {/* Form */}
      <div style={cardStyle}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Decision</div>

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800 }}>
            <input
              type="radio"
              name="decision"
              checked={decision === "APPROVE"}
              onChange={() => setDecision("APPROVE")}
              disabled={busy || phase !== "FORM"}
            />
            Approve
          </label>

          <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800 }}>
            <input
              type="radio"
              name="decision"
              checked={decision === "REJECT"}
              onChange={() => setDecision("REJECT")}
              disabled={busy || phase !== "FORM"}
            />
            Reject
          </label>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 800 }}>
            Reject Reason {decision === "REJECT" ? "(required)" : "(disabled)"}
          </div>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            disabled={busy || phase !== "FORM" || decision !== "REJECT"}
            style={{
              width: "100%",
              minHeight: 90,
              marginTop: 6,
              padding: 10,
              borderRadius: 10,
              border: "1px solid #e2e8f0",
              background: busy || phase !== "FORM" || decision !== "REJECT" ? "#f8fafc" : "white",
            }}
          />
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800 }}>
            <input
              type="checkbox"
              checked={!!useMySignature}
              onChange={(e) => setUseMySignature(e.target.checked)}
              disabled={busy || phase !== "FORM"}
            />
            Use my profile signature
          </label>

          <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
            If checked, your approval will use the signature stored in your Profile.
          </div>
        </div>

        <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            onClick={submitDecision}
            disabled={busy || !wrId || phase !== "FORM"}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #e2e8f0",
              fontWeight: 900,
              background: "#0f172a",
              color: "white",
              opacity: busy ? 0.7 : 1,
              cursor: busy || phase !== "FORM" ? "not-allowed" : "pointer",
            }}
          >
            {busy ? "Submitting..." : "Submit"}
          </button>

          {phase !== "FORM" ? (
            <button
              onClick={() => {
                setPhase("FORM");
                setError("");
              }}
              disabled={busy}
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                border: "1px solid #e2e8f0",
                fontWeight: 900,
                background: "white",
                cursor: busy ? "not-allowed" : "pointer",
              }}
            >
              Back to form
            </button>
          ) : null}
        </div>

        <div style={{ marginTop: 12, fontSize: 12, color: "#64748b" }}>
          Sales Admin will receive an email when the PDFs are ready.
        </div>
      </div>
    </div>
  );
}
